<template>
	<form
		ref="contactform"
		method="post"
		enctype="multipart/form-data"
		:class="props.name"
		class="contact-form"
		@submit.prevent="submit()"
	>
		<span v-if="state.error" class="error">
			{{ $t(`form${capitalize(state.error)}`) }}
		</span>
		<div v-for="(field, idx) in form" :key="field.name" class="field" :class="`field-${idx + 1}`">
			<div v-if="field.name?.toLowerCase().endsWith('date')">
				<client-only>
					<date-picker
						v-model:value="state[field.name]"
						value-type="format"
						format="DD-MM-YYYY"
						class=""
						:lang="datepickerLang"
						:placeholder="$t(`form${capitalize(field.name)}`) + (field.required ? ' *' : '')"
					>
						<template #icon-calendar> &nbsp; </template>
					</date-picker>
				</client-only>
			</div>
			<div v-else-if="field.edittype == 'radiogroup'" class="formgroup-container">
				<div class="formgroup-heading">
					<strong>{{ $t(`form${capitalize(field.name)}`) }}<span v-if="field.required"> *</span></strong>
				</div>
				<div v-if="field.hasExtraText" class="formgroup-text">
					{{ $t(`form${capitalize(field.name)}Extra`) }}
				</div>
				<div class="radiobuttons">
					<div v-for="(value, key) in field.options" :key="key">
						<label>
							<input v-model="field.value" type="radio" :name="field.name" :value="key" />{{
								$t(`form${capitalize(key)}`)
							}}
						</label>
					</div>
					<div v-if="!field.required">
						<label> <input type="radio" :name="field.name" value="" />{{ $t('formNoPreference') }} </label>
					</div>
				</div>
			</div>
			<div v-else-if="field.edittype == 'fileuploader'">
				<label :for="field.name" class="fileupload-group">
					<span class="label-text">{{ $t('formCV') }}<span v-if="field.required"> *</span></span>
					<div class="dropzone">
						<div class="dropzone-icon">
							<font-awesome-icon :icon="['fas', 'upload']" />
						</div>
						<input
							:id="field.name"
							:key="field.name"
							type="file"
							accept=".doc,.docx,.pdf,.odt"
							:name="field.name"
							:required="field.required"
							@change="onFileChange"
						/>
					</div>
				</label>
			</div>
			<div v-else-if="field.edittype == 'checkbox'" class="formgroup-container">
				<div class="checkboxes">
					<label :for="field.name">
						<input
							v-model="field.value"
							:name="field.name"
							type="checkbox"
							:checked="!!field.value"
							:placeholder="$t(`form${capitalize(field.name)}`)"
							:required="field.required"
						/>
						<span>{{ $t(`form${capitalize(field.name)}`) }}</span>
					</label>
				</div>
			</div>
			<div v-else-if="field.edittype == 'list'" class="select">
				<select :id="field.name" v-model="field.value" :name="field.name">
					<option v-if="!field.required" value="null" selected>
						{{ $t(`form${capitalize(field.name)}`) }}
					</option>
					<option v-for="(value, key) in field.options" :key="key" :value="key">
						{{ value }}
					</option>
				</select>
			</div>
			<div v-else-if="field.edittype == 'textarea'">
				<textarea
					:key="field.name"
					v-model="field.value"
					:name="field.name"
					:placeholder="$t(`form${capitalize(field.name)}`) + (field.required ? ' *' : '')"
					:required="field.required"
				/>
			</div>
			<div v-else>
				<input
					:key="field.name"
					v-model="field.value"
					:name="field.name"
					:required="field.required"
					:placeholder="$t(`form${capitalize(field.name)}`) + (field.required ? ' *' : '')"
					:type="state.inputTypes[field.name] || 'text'"
				/>
			</div>
		</div>
		<input type="submit" class="button" :value="$t('formSubmit')" />
	</form>
</template>

<script setup>
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import datePickerTranslations from '~/i18n/datePicker.ts';

const { locale } = useI18n();
const { $installRecaptcha, $executeRecaptcha } = useNuxtApp();

const contactform = ref(null);

const props = defineProps({
	form: { type: Array, default: () => [] },
	name: { type: String, default: '' },
	extraQuerydata: { type: Object, default: () => {} },
});

const state = useState('state', () => ({
	eventDate: '',
	arrivalDate: '',
	CV: '',
	departureDate: '',
	inputTypes: { email: 'email', phone: 'tel' },
	error: '',
}));

const datepickerLang = computed(() => datePickerTranslations[locale.value] ?? datePickerTranslations.en);

const createStateData = (state) => {
	const stateData = {
		eventDate: state.eventDate,
		arrivalDate: state.arrivalDate,
		departureDate: state.departureDate,
	};

	return stateData;
};

const submit = async () => {
	const formData = new FormData();

	props.form.forEach((item) => formData.append(item.name, item.value || ''));

	getFieldsByEditType('fileuploader').forEach((item) => {
		if (state.value[item.name]) {
			formData.set(item.name, state.value[item.name]);
		}
	});

	const stateData = createStateData(state.value);
	Object.entries(stateData).forEach(([key, value]) => {
		formData.append(key, value);
	});

	if (window.recaptcha) {
		formData.append('g-recaptcha-response', await $executeRecaptcha());
	}

	const { data: result } = await useWebsiteFetch(props.name, {
		method: 'POST',
		body: formData,
		query: { language: locale.value, ...props.extraQuerydata },
	});

	if (result.value.status === 'error') {
		state.value.error = result.error;
	} else if (result.value.redirect) {
		contactform.value.reset();
		navigateTo(result.value.redirect);
	}
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const onFileChange = (e) => {
	const file = e.target.files[0] ?? null;
	if (file) {
		state.value.CV = file;
	}
};

const getFieldsByEditType = (edittype) => {
	return props.form.filter((item) => item.edittype === edittype);
};

onMounted(async () => {
	$installRecaptcha();
});
</script>

<style lang="scss" scoped>
.meetingform {
	display: grid;
	grid-template-columns: repeat(6, 1fr);
	gap: 15px;

	.field-1,
	.field-2,
	.field-6,
	.field-7,
	.field-8,
	.field-9 {
		grid-column: span 3;
	}

	.field-3,
	.field-4,
	.field-5 {
		grid-column: span 2;
	}

	.field-10,
	.field-14,
	.field-15,
	.field-16 {
		grid-column: span 6;
	}

	.field-11,
	.field-12,
	.field-13 {
		grid-column: span 2;
	}
}

.mx-datepicker {
	width: 100%;
}

form {
	margin: 40px 0 0;

	.error {
		display: block;
		margin: 0 0 10px;
	}

	:deep(input:not([type='checkbox'], [type='radio'], [type='submit'], [type='file'])),
	select,
	textarea,
	.fileupload-group {
		color: #555;
		width: 100%;
		padding: 25px;
		border: none;
		margin: 0 0 15px;
		box-shadow: none;
		outline: none;
		font-size: var(--body-font-size);
		font-family: var(--body-font-family);
	}

	:deep(input:not([disabled], [type='submit'], [type='file'])),
	select,
	textarea,
	.fileupload-group {
		background-color: #fff;
		border-radius: 30px;
	}

	select {
		background: url('~/assets/images/icon-form-dropdown.png') no-repeat #fff;
		background-position: right 15px top 50%;
		appearance: none;
		border-radius: 30px;
	}

	input[type='submit'] {
		width: auto;
	}

	textarea {
		min-height: 140px;
		max-width: 100%;
	}

	.fileupload-group {
		position: relative;
		display: block;
		cursor: pointer;

		.dropzone {
			display: flex;
			margin: 15px 0 0;
			color: #555;
			overflow: hidden;

			input {
				cursor: pointer;
				max-width: 100%;
			}

			::-webkit-file-upload-button,
			::file-selector-button {
				display: none;
			}

			.dropzone-icon {
				margin-right: 10px;
			}
		}

		.label-text {
			color: #139375;
			display: block;
			text-align: left;
		}
	}

	:deep(.mx-input) {
		height: auto;
		font-size: unset;
		color: unset;

		&:focus,
		&:hover {
			border-color: transparent;
		}

		&:focus {
			outline: revert;
		}
	}

	.button {
		width: auto;
		appearance: none;
		cursor: pointer;
		outline: none;
		color: var(--body-color);
		border: 1px solid var(--body-color);
		padding: 4px 35px 5px;
		background: none;
		font-size: 14px;

		&:hover {
			color: #fff;
			background: var(--body-color);
		}
	}

	.formgroup-container {
		margin: 0 0 25px;

		.formgroup-heading {
			margin-bottom: 15px;
		}

		.checkboxes {
			display: flex;
			margin: 5px 0 10px;

			span {
				width: calc(100% - 40px);
			}

			&.block {
				display: block;
			}

			.extra-option {
				margin: 15px 0 0;
			}

			label {
				width: auto;
				padding: 0;
				font-size: 0.9em;
				margin: 0 5px 0 0;
				display: flex;
				gap: 10px;
				align-items: center;

				input {
					width: 20px;
					padding: 0;
					appearance: auto;
					margin: -2px 6px 0 0;
				}
			}
		}

		.radiobuttons {
			display: flex;
			flex-flow: row wrap;
			margin: 5px 0 10px;

			> div {
				width: 100%;
				margin: 0 0 10px;
			}

			label,
			input {
				width: auto;
				padding: 0;
				font-size: 0.9em;
			}

			label {
				margin: 0 5px 0 0;
				display: flex;
				gap: 10px;

				input {
					width: auto;
					appearance: auto;
					margin: -2px 6px 0 0;
					padding: 0;
				}
			}
		}
	}
}

@media (max-width: 800px) {
	.meetingform > .field {
		grid-column: span 6;
	}
}
</style>
